/**
 * Tester feedback (crossmarc/steward-app#2531): a TEMPORARY "Send feedback" button on the intake,
 * onboarding and requests screens, so a firm testing those screens (RIPL, 2026-10) can leave a
 * note from wherever they are without naming the page. Each note becomes a GitHub issue in
 * `crossmarc/steward-app` labelled `tester-feedback`, which a Claude Code session can read and
 * act on directly.
 *
 * Remove the whole feature when the firm's testing round ends: this file, the button
 * (`src/testerfeedback/`), `POST /tester-feedback` (`handlers/testerFeedback.ts`), the filer
 * Lambda (`testerFeedbackFiler.ts`) and its wiring in `deploy/lib/api-stack.ts`.
 *
 * Two shapes live here so the browser, the public route and the filer Lambda cannot drift:
 *
 *   - {@link TesterFeedbackRequestSchema}: what the browser sends.
 *   - {@link TesterFeedbackPayloadSchema}: the ONE payload the filer accepts on a synchronous
 *     invoke from the public function. Strict, so that function cannot be made to do anything
 *     else by a payload naming another operation.
 *
 * What the context carries is a closed list of short strings describing WHERE the person was:
 * never an answer, a draft, a query string or a screenshot (these screens show tax ids and
 * account numbers). `src/testerfeedback/TesterFeedback.test.tsx` pins what the browser collects.
 */
import { z } from 'zod';
import { INTAKE_HONEYPOT_FIELD } from './intakeSchema.js';

/**
 * The firms whose screens show the button, by compiled firm id (`ADVISOR_FIRMS`). The browser
 * reads it to decide whether to draw the button; the public route and the filer both refuse a
 * firm not on it. Turning the feature off for a firm is removing it here (a deploy); turning it
 * off everywhere at once without a deploy is deleting the GitHub token parameter, which the
 * filer reads as "not provisioned" and refuses every note.
 */
export const TESTER_FEEDBACK_FIRM_IDS: readonly string[] = ['ripl'];

export function testerFeedbackEnabledFor(firmId: string | null | undefined): boolean {
  return typeof firmId === 'string' && TESTER_FEEDBACK_FIRM_IDS.includes(firmId);
}

/** The optional kind chip. */
export const TESTER_FEEDBACK_CATEGORIES = ['problem', 'idea', 'wording'] as const;
export type TesterFeedbackCategory = (typeof TESTER_FEEDBACK_CATEGORIES)[number];

/** Where the button was pressed: the two screen families it is mounted on. */
export const TESTER_FEEDBACK_SURFACES = ['intake', 'onboarding'] as const;
export type TesterFeedbackSurface = (typeof TESTER_FEEDBACK_SURFACES)[number];

export const TESTER_FEEDBACK_MESSAGE_MAX = 4000;

/** Past this many bytes the public route does not parse the body. Above the largest valid one (about 14 KB, 2026-10-23). */
export const TESTER_FEEDBACK_MAX_BODY_BYTES = 16 * 1024;

/** Version of the filer payload. A strict literal: a mismatched deploy fails closed. */
export const TESTER_FEEDBACK_PAYLOAD_VERSION = 1;

/** No control characters except tab and newline (a note may run to several lines). */
const MESSAGE_SAFE = /^[^\u0000-\u0008\u000B-\u001F\u007F-\u009F]+$/u;
/** No control characters at all in a one-line context field. */
const LINE_SAFE = /^[^\u0000-\u001F\u007F-\u009F]*$/u;

const line = (max: number) => z.string().trim().max(max).regex(LINE_SAFE);

export const TesterFeedbackContextSchema = z.strictObject({
  surface: z.enum(TESTER_FEEDBACK_SURFACES),
  /** The screen as the code names it, e.g. `Intake form: Cash accounts`. */
  screen: line(160),
  /** The page's main heading as shown, the fallback when no screen label was set. */
  heading: line(200),
  /** `location.pathname` only. A `?` or `#` is refused rather than trimmed. */
  path: line(300).regex(/^\/[^?#]*$/),
  host: line(253),
  /** `VITE_APP_VERSION`: a `rel-*` tag, `dev-<sha>` or `local`. */
  release: line(80),
  device: z.enum(['phone', 'desktop']),
  /** `innerWidth x innerHeight`, e.g. `390x844`. */
  viewport: z.string().regex(/^\d{1,5}x\d{1,5}$/),
  userAgent: line(400),
  /** The signed-in login's address as the browser holds it. Reported, not verified. */
  email: z.email().max(254).optional(),
});
export type TesterFeedbackContext = z.infer<typeof TesterFeedbackContextSchema>;

export const TesterFeedbackFieldsSchema = z.strictObject({
  message: z.string().trim().min(1).max(TESTER_FEEDBACK_MESSAGE_MAX).regex(MESSAGE_SAFE),
  category: z.enum(TESTER_FEEDBACK_CATEGORIES).optional(),
  firmId: z.string().regex(/^[a-z0-9-]{1,64}$/),
  context: TesterFeedbackContextSchema,
});
export type TesterFeedbackFields = z.infer<typeof TesterFeedbackFieldsSchema>;

/** The browser's body for `POST /tester-feedback`: the fields plus the honeypot. */
export const TesterFeedbackRequestSchema = z.strictObject({
  ...TesterFeedbackFieldsSchema.shape,
  [INTAKE_HONEYPOT_FIELD]: z.string().max(200).optional(),
});
export type TesterFeedbackRequest = z.infer<typeof TesterFeedbackRequestSchema>;

/** What the public function hands the filer. `env` and `receivedAt` are the server's own. */
export const TesterFeedbackPayloadSchema = z.strictObject({
  v: z.literal(TESTER_FEEDBACK_PAYLOAD_VERSION),
  op: z.literal('file'),
  requestId: z.string().regex(/^[A-Za-z0-9=_.-]{0,200}$/),
  env: z.string().regex(/^[a-z0-9-]{1,32}$/),
  receivedAt: z.iso.datetime(),
  feedback: TesterFeedbackFieldsSchema,
});
export type TesterFeedbackPayload = z.infer<typeof TesterFeedbackPayloadSchema>;

/** The filer's answer. `reason` is a closed set so the public route can log it. */
export const TesterFeedbackFilerResultSchema = z.discriminatedUnion('filed', [
  z.strictObject({ filed: z.literal(true) }),
  z.strictObject({
    filed: z.literal(false),
    reason: z.enum(['invalid_payload', 'firm_not_enabled', 'not_provisioned', 'daily_cap', 'github_error']),
  }),
]);
export type TesterFeedbackFilerResult = z.infer<typeof TesterFeedbackFilerResultSchema>;
